
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import * as React from "react"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Define the return type with the cancel method
export interface DebouncedFunction<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): void;
  cancel: () => void;
}

// Enhanced debounce function with better control and longer default timeout
export function debounce<T extends (...args: any[]) => any>(
  func: T, 
  wait: number = 500, 
  options: { leading?: boolean } = {}
): DebouncedFunction<T> {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;
  
  const debounced = function(...args: Parameters<T>) {
    lastArgs = args;
    
    const later = () => {
      timeout = null;
      if (lastArgs !== null) {
        func(...lastArgs);
        lastArgs = null;
      }
    };
    
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    
    // Execute immediately if leading option is true and there's no active timeout
    if (options.leading && timeout === null) {
      func(...args);
    }
    
    timeout = setTimeout(later, wait);
  };
  
  // Add cancel method to handle cleanup
  debounced.cancel = function() {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
      lastArgs = null;
    }
  };
  
  // TypeScript won't infer this properly so we need to cast it
  return debounced as DebouncedFunction<T>;
}

/**
 * NEW: Ensure Radix UI components render correctly with the proper z-index
 * and background colors across different environments
 * 
 * @param selector The CSS selector for the Radix UI component root
 * @param zIndex Optional z-index to apply to the element
 */
export function fixRadixUIRendering(selector: string, zIndex?: number): void {
  if (typeof window === 'undefined') return;
  
  // Apply after a brief delay to ensure elements are in the DOM
  setTimeout(() => {
    const elements = document.querySelectorAll(selector);
    
    elements.forEach(el => {
      // Ensure proper background and z-index for portals
      if (el instanceof HTMLElement) {
        if (el.style.position === 'fixed') {
          // Ensure fixed positioned elements have proper background
          if (!el.style.backgroundColor || el.style.backgroundColor === 'transparent') {
            el.style.backgroundColor = 'var(--background, white)';
          }
          
          // Apply z-index if specified or ensure it has a minimum value
          if (zIndex !== undefined) {
            el.style.zIndex = zIndex.toString();
          } else if (!el.style.zIndex || parseInt(el.style.zIndex, 10) < 50) {
            el.style.zIndex = '50';
          }
        }
      }
    });
  }, 50);
}

/**
 * NEW: Easily handle Radix component mounting issues by forcing rerender
 * Returns a function that when called will trigger a component rerender
 */
export function useForceRadixRerender(): () => void {
  const [, setRender] = React.useState(0);
  
  return React.useCallback(() => {
    setRender(prev => prev + 1);
  }, []);
}

/**
 * NEW: Utility to patch fullcalendar-specific styles to ensure proper rendering
 */
export function patchFullCalendarStyles(): void {
  if (typeof window === 'undefined') return;
  
  const styleElement = document.getElementById('fullcalendar-patch-styles');
  if (styleElement) return; // Already patched
  
  const style = document.createElement('style');
  style.id = 'fullcalendar-patch-styles';
  style.innerHTML = `
    .fc-theme-standard .fc-scrollgrid {
      border: 1px solid #ddd !important;
      background-color: var(--background, white) !important; 
    }
    .fc-theme-standard td, .fc-theme-standard th {
      border: 1px solid #ddd !important;
      background-color: var(--background, white) !important;
    }
    .fc-scroller-harness, .fc-scroller {
      background-color: var(--background, white) !important;
    }
    .fc-view-harness {
      background-color: var(--background, white) !important;
    }
  `;
  document.head.appendChild(style);
}
